import { apiUtils } from '../hooks';
import { API_ENDPOINTS } from './apiEndpoints';

interface UploadProgressEvent {
  loaded: number;
  total?: number;
}

export interface PillarScore {
  total: number;
  skipped: number;
  answered: number;
  total_questions: number;
  engagement_percentage: number;
}

export interface EngagementScore {
  e: PillarScore;
  s: PillarScore;
  g: PillarScore;
  alo: PillarScore;
}

export interface GBTAUploadData {
  attachment_id: string;
  original_filename: string;
  preview_url: string;
  engagement_score: EngagementScore;
}

interface UploadGBTAResponse {
  success: true;
  message: string;
  data: GBTAUploadData;
}

interface GBTAResponse {
  status: 'success';
  data: GBTAUploadData;
  message: 'GBTA data fetched successfully';
}

class GBTAService {
  /**
   * Fetch GBTA data for a hotel
   */
  async fetchGbtaData(hotelId: string): Promise<GBTAUploadData | null> {
    try {
      const response = await apiUtils.get<GBTAResponse>(
        API_ENDPOINTS.gbta.getAttachment(hotelId)
      );

      if (response && response.data && response.data.data) {
        return response.data.data;
      }

      return null;
    } catch (err: any) {
      // If it's a 404, return null (no GBTA data exists)
      if (err.response?.status === 404) {
        return null;
      }
      throw err;
    }
  }

  /**
   * Upload a GBTA file for a hotel
   */
  async uploadGbtaFile(
    hotelId: string,
    file: File,
    onUploadProgress?: (progressEvent: UploadProgressEvent) => void
  ): Promise<GBTAUploadData> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiUtils.post<UploadGBTAResponse>(
      API_ENDPOINTS.gbta.upload(hotelId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      }
    );

    return response.data.data;
  }

  /**
   * Delete a GBTA attachment for a hotel
   */
  async deleteGbtaFile(hotelId: string, attachmentId: string): Promise<void> {
    await apiUtils.delete(
      API_ENDPOINTS.gbta.deleteAttachment(hotelId, attachmentId)
    );
  }
}

export const gbtaServiceInstance = new GBTAService();
